import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const FILTROS_INICIAIS = { status: '', id_cliente: '', topic: '', busca: '', dias: '7' };

const STATUS_BADGE = {
  success: { classe: 'bg-success', rotulo: 'Sucesso' },
  error: { classe: 'bg-danger', rotulo: 'Erro' },
  ignored: { classe: 'bg-secondary', rotulo: 'Ignorado' },
};

const WebhookEventos = () => {
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [eventos, setEventos] = useState([]);
  const [resumo, setResumo] = useState({ success: 0, error: 0, ignored: 0 });
  const [topics, setTopics] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [carregando, setCarregando] = useState(true);
  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [resumoTitulo, setResumoTitulo] = useState(null);
  const [carregandoResumo, setCarregandoResumo] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
  });

  const montarParams = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) p.append(k, v); });
    p.append('page', extra.page || page);
    p.append('page_size', pageSize);
    return p.toString();
  }, [filtros, page, pageSize]);

  const carregar = useCallback(async (paginaAlvo) => {
    setCarregando(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/webhook-eventos/?${montarParams({ page: paginaAlvo })}`,
        authHeader()
      );
      setEventos(resp.data.eventos || []);
      setResumo(resp.data.resumo || { success: 0, error: 0, ignored: 0 });
      setTopics(resp.data.topics || []);
      setClientes(resp.data.clientes || []);
      setTotal(resp.data.total || 0);
      if (resp.data.aviso) toast.info(resp.data.aviso);
    } catch (e) {
      toast.error('Erro ao carregar os eventos do webhook.');
    } finally {
      setCarregando(false);
    }
  }, [montarParams]);

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarFiltros = () => {
    setPage(1);
    carregar(1);
  };

  const limparFiltros = () => {
    setFiltros(FILTROS_INICIAIS);
    setPage(1);
    setTimeout(() => carregar(1), 0);
  };

  const mudarPagina = (nova) => {
    setPage(nova);
    carregar(nova);
  };

  const abrirDetalhe = async (id) => {
    setCarregandoDetalhe(true);
    setDetalhe({ id });
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/webhook-eventos/?id=${id}`,
        authHeader()
      );
      setDetalhe(resp.data.evento);
    } catch (e) {
      toast.error('Erro ao carregar o payload do evento.');
      setDetalhe(null);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const abrirResumoTitulo = async (ev) => {
    if (!ev.codigo || !ev.id_cliente) return;
    setCarregandoResumo(true);
    setResumoTitulo({ codigo: ev.codigo, nome_cliente: ev.nome_cliente });
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/webhook-eventos/titulo/?id_cliente=${ev.id_cliente}&codigo=${ev.codigo}`,
        authHeader()
      );
      setResumoTitulo({ ...resp.data, nome_cliente: ev.nome_cliente });
    } catch (e) {
      toast.error('Erro ao carregar o resumo do título.');
      setResumoTitulo(null);
    } finally {
      setCarregandoResumo(false);
    }
  };

  const filtrarPorStatus = (status) => {
    const novo = { ...filtros, status: filtros.status === status ? '' : status };
    setFiltros(novo);
    setPage(1);
    setTimeout(() => carregar(1), 0);
  };

  const formatarPayload = (payload) => {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch (e) {
      return payload || '';
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="container-fluid py-4">

      {/* Header padrão da plataforma */}
      <div className="card mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0" style={{ color: '#212529' }}>
            <i className="fas fa-tower-broadcast me-2" style={{ color: '#d4af37' }}></i>
            Webhook Omie — Eventos
          </h5>
          <small style={{ color: '#6c757d' }}>
            Tudo que o webhook recebeu dos clientes Omie: sucessos, erros e ignorados
          </small>
          <div style={{ width: 60, height: 3, background: '#06b6d4', marginTop: 8, borderRadius: 2 }}></div>
        </div>

        <div className="card-body">
          {/* Cards de resumo (clicáveis = filtro rápido) */}
          <div className="row g-2 mb-3">
            {[
              { chave: 'success', titulo: 'Processados', cor: '#198754', icone: 'fa-circle-check' },
              { chave: 'error', titulo: 'Com erro', cor: '#dc3545', icone: 'fa-circle-xmark' },
              { chave: 'ignored', titulo: 'Ignorados', cor: '#6c757d', icone: 'fa-circle-minus' },
            ].map((c) => (
              <div className="col-md-4" key={c.chave}>
                <div
                  className="card h-100"
                  role="button"
                  onClick={() => filtrarPorStatus(c.chave)}
                  style={{
                    cursor: 'pointer',
                    border: filtros.status === c.chave ? `2px solid ${c.cor}` : '1px solid #dee2e6',
                  }}
                >
                  <div className="card-body py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <small style={{ color: '#6c757d' }}>{c.titulo} (período)</small>
                      <h4 className="mb-0" style={{ color: c.cor }}>{resumo[c.chave] || 0}</h4>
                    </div>
                    <i className={`fas ${c.icone} fa-2x`} style={{ color: c.cor, opacity: 0.35 }}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Filtros */}
          <div className="row g-2">
            <div className="col-md-2">
              <select className="form-select" value={filtros.dias}
                onChange={(e) => setFiltros((p) => ({ ...p, dias: e.target.value }))}>
                <option value="1">Últimas 24h</option>
                <option value="7">Últimos 7 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="90">Últimos 90 dias</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={filtros.status}
                onChange={(e) => setFiltros((p) => ({ ...p, status: e.target.value }))}>
                <option value="">Todos status</option>
                <option value="success">Sucesso</option>
                <option value="error">Erro</option>
                <option value="ignored">Ignorado</option>
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filtros.id_cliente}
                onChange={(e) => setFiltros((p) => ({ ...p, id_cliente: e.target.value }))}>
                <option value="">Todos clientes</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome || `Cliente ${c.id}`}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filtros.topic}
                onChange={(e) => setFiltros((p) => ({ ...p, topic: e.target.value }))}>
                <option value="">Todos eventos (topic)</option>
                {topics.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <input type="text" className="form-control" placeholder="Código / mensagem"
                value={filtros.busca}
                onChange={(e) => setFiltros((p) => ({ ...p, busca: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && aplicarFiltros()} />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-dark" onClick={aplicarFiltros}>Filtrar</button>
            <button className="btn btn-outline-secondary" onClick={limparFiltros}>Limpar</button>
            <button className="btn btn-outline-dark ms-auto" onClick={() => carregar(page)}>
              <i className="fas fa-rotate me-2"></i>Atualizar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card">
        <div className="card-body">
          {carregando ? (
            <p style={{ color: '#6c757d' }}><i className="fas fa-spinner fa-spin me-2"></i>Carregando...</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Data/Hora</th>
                      <th>Cliente</th>
                      <th>Evento (topic)</th>
                      <th>Código</th>
                      <th>Status</th>
                      <th>Mensagem</th>
                      <th className="text-end">Tempo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventos.map((ev) => {
                      const badge = STATUS_BADGE[ev.status] || { classe: 'bg-secondary', rotulo: ev.status };
                      return (
                        <tr key={ev.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{String(ev.data_hora).slice(0, 19).replace('T', ' ')}</td>
                          <td>{ev.nome_cliente || <span className="text-muted">appKey {ev.app_key || '—'}</span>}</td>
                          <td><small>{ev.topic || '—'}</small></td>
                          <td>
                            {ev.codigo ? (
                              <button
                                className="btn btn-link btn-sm p-0"
                                style={{ color: '#00205b', textDecoration: 'underline', fontWeight: 600 }}
                                title="Ver resumo do título"
                                onClick={() => abrirResumoTitulo(ev)}
                              >
                                {ev.codigo}
                              </button>
                            ) : '—'}
                          </td>
                          <td><span className={`badge ${badge.classe}`}>{badge.rotulo}</span></td>
                          <td><small className="text-muted">{ev.mensagem || '—'}</small></td>
                          <td className="text-end"><small>{ev.tempo_ms != null ? `${ev.tempo_ms} ms` : '—'}</small></td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-dark" title="Ver payload"
                              onClick={() => abrirDetalhe(ev.id)}>
                              <i className="fas fa-code"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {eventos.length === 0 && (
                <p className="text-center text-muted my-4">
                  Nenhum evento no período. Assim que o webhook receber eventos do Omie, eles aparecem aqui.
                </p>
              )}

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small style={{ color: '#6c757d' }}>
                  {total} evento(s) — página {page} de {totalPaginas}
                </small>
                <div className="btn-group">
                  <button className="btn btn-sm btn-outline-dark" disabled={page <= 1}
                    onClick={() => mudarPagina(page - 1)}>Anterior</button>
                  <button className="btn btn-sm btn-outline-dark" disabled={page >= totalPaginas}
                    onClick={() => mudarPagina(page + 1)}>Próxima</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de resumo do título */}
      {resumoTitulo && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1070 }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-white">
                  <div>
                    <h5 className="modal-title" style={{ color: '#212529' }}>
                      Resumo do título {resumoTitulo.codigo}
                    </h5>
                    {resumoTitulo.nome_cliente && (
                      <small style={{ color: '#6c757d' }}>{resumoTitulo.nome_cliente}</small>
                    )}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setResumoTitulo(null)}></button>
                </div>
                <div className="modal-body">
                  {carregandoResumo ? (
                    <p style={{ color: '#6c757d' }}><i className="fas fa-spinner fa-spin me-2"></i>Carregando resumo...</p>
                  ) : (
                    <>
                      {/* O que aconteceu (texto) */}
                      {(resumoTitulo.resumo || []).map((linha, i) => (
                        <div
                          key={i}
                          className={`alert py-2 mb-2 ${linha.startsWith('') ? 'alert-warning' : 'alert-light border'}`}
                        >
                          <small>{linha}</small>
                        </div>
                      ))}

                      {/* Situação atual do título */}
                      {resumoTitulo.titulo && (
                        <div className="row g-2 my-2">
                          {[
                            { rotulo: 'Status', valor: resumoTitulo.titulo.status_titulo },
                            { rotulo: 'Valor', valor: resumoTitulo.titulo.valor_documento && `R$ ${Number(resumoTitulo.titulo.valor_documento).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
                            { rotulo: 'Vencimento', valor: (resumoTitulo.titulo.data_vencimento || '').slice(0, 10).split('-').reverse().join('/') },
                            { rotulo: resumoTitulo.tipo === 'pagar' ? 'Fornecedor' : 'Cliente', valor: resumoTitulo.titulo.nome_cliente },
                            { rotulo: 'NF', valor: resumoTitulo.titulo.numero_documento_fiscal },
                            { rotulo: 'Documento', valor: resumoTitulo.titulo.numero_documento },
                          ].filter((c) => c.valor).map((c) => (
                            <div className="col-md-4" key={c.rotulo}>
                              <div className="border rounded p-2 h-100">
                                <small className="text-muted d-block">{c.rotulo}</small>
                                <strong style={{ fontSize: 14 }}>{c.valor}</strong>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Linha do tempo dos eventos */}
                      <h6 className="mt-3 mb-2" style={{ color: '#212529' }}>Linha do tempo</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Data/Hora</th>
                              <th>O que aconteceu</th>
                              <th>Status</th>
                              <th>Mensagem</th>
                              <th className="text-end">Tempo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(resumoTitulo.eventos || []).map((e) => {
                              const badge = STATUS_BADGE[e.status] || { classe: 'bg-secondary', rotulo: e.status };
                              return (
                                <tr key={e.id}>
                                  <td style={{ whiteSpace: 'nowrap' }}><small>{String(e.data_hora).slice(0, 19)}</small></td>
                                  <td><small>{e.acao}</small></td>
                                  <td><span className={`badge ${badge.classe}`}>{badge.rotulo}</span></td>
                                  <td><small className="text-muted">{e.mensagem || '—'}</small></td>
                                  <td className="text-end"><small>{e.tempo_ms != null ? `${e.tempo_ms} ms` : '—'}</small></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setResumoTitulo(null)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setResumoTitulo(null)}></div>
        </>
      )}

      {/* Modal de payload */}
      {detalhe && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1070 }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-white">
                  <div>
                    <h5 className="modal-title" style={{ color: '#212529' }}>
                      Evento #{detalhe.id}{detalhe.topic ? ` — ${detalhe.topic}` : ''}
                    </h5>
                    {detalhe.nome_cliente && (
                      <small style={{ color: '#6c757d' }}>
                        {detalhe.nome_cliente} · {String(detalhe.data_hora || '').slice(0, 19).replace('T', ' ')}
                      </small>
                    )}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
                </div>
                <div className="modal-body">
                  {carregandoDetalhe ? (
                    <p style={{ color: '#6c757d' }}><i className="fas fa-spinner fa-spin me-2"></i>Carregando payload...</p>
                  ) : (
                    <>
                      {detalhe.mensagem && (
                        <div className={`alert ${detalhe.status === 'error' ? 'alert-danger' : 'alert-light'} py-2`}>
                          <small>{detalhe.mensagem}</small>
                        </div>
                      )}
                      <pre style={{
                        background: '#0e1726', color: '#e2e8f0', padding: 16,
                        borderRadius: 8, fontSize: 12, maxHeight: '55vh', overflow: 'auto',
                      }}>
                        {formatarPayload(detalhe.payload)}
                      </pre>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setDetalhe(null)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setDetalhe(null)}></div>
        </>
      )}
    </div>
  );
};

export default WebhookEventos;
